import { isEqual } from 'lodash';
import { targetingConstants } from 'sonata-platform-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { getMediaQualityResetValues } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/getMediaQualityResetValues.helper';
import { AppSitePublishersConfiguration, TargetingContext } from '@/contexts/Targeting/TargetingContext';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { RetargetingSegmentType } from 'sonata-static-storage/lib/schemas/targeting/Audience/Audience.types';
import type {
  TargetingBaseType,
  TargetingWithRetargetingType
} from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { AudienceGroup } from '@/components/Targeting/AudienceTargeting/AudienceTargeting';

const EMPTY_STRICT_SEARCHES: AppSitePublishersConfiguration = {
  blacklistedStrictSearches: [],
  whitelistedStrictSearches: []
};

const {
  DEFAULT_AD_EXCHANGES,
  DEFAULT_APP_AND_SITE_CHANNEL,
  DEFAULT_APP_AND_SITE_CONFIGURATION,
  DEFAULT_APP_AND_SITES_CATEGORIES,
  DEFAULT_AUDIENCE,
  DEFAULT_CONTEXTUAL_TARGETING,
  DEFAULT_DEALS_CONFIGURATION,
  DEFAULT_DEMOGRAPHY,
  DEFAULT_DEVICE_TARGETING,
  DEFAULT_DOOH_AD_EXCHANGES,
  DEFAULT_LQI,
  DEFAULT_MEDIA_QUALITY,
  DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY,
  DEFAULT_RETARGETING,
  DEFAULT_TEMPERATURE_AND_WEATHER
} = targetingConstants;

interface TargetingProviderProps {
  budget?: number;
  children: JSX.Element | JSX.Element[];
  countries?: string[];
  currency?: BaseCurrencyInterface;
  familyType?: CampaignFamilyEnum;
  onChange: (targeting: TargetingBaseType) => void;
  showDeals?: boolean;
  targeting: TargetingBaseType;
  user: UserInterface;
  userRoles: UserRoleInterface[];
}

function TargetingProvider({
  budget,
  children,
  countries,
  currency,
  familyType,
  onChange,
  showDeals,
  targeting,
  user,
  userRoles
}: Readonly<TargetingProviderProps>) {
  const emitTargetingIfApply = (newTargeting: TargetingBaseType) => {
    if (isEqual(targeting, newTargeting)) {
      return;
    }
    onChange(newTargeting);
  };

  const setProp = (prop: any, value: any) => {
    const newTargeting = {
      ...targeting,
      [prop]: value
    };
    emitTargetingIfApply(newTargeting);
  };

  const setAppSiteConfigurationProps = (config: AppSitePublishersConfiguration) => {
    const newTargeting = {
      ...targeting,
      appSiteConfiguration: {
        ...targeting.appSiteConfiguration,
        ...config
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setDeals = (dealsSelected: string[]) => {
    const newTargeting = {
      ...targeting,
      deals: dealsSelected
    };
    emitTargetingIfApply(newTargeting);
  };

  const setAudience = (audienceGroups: AudienceGroup[] = []) => {
    const newTargeting = {
      ...targeting,
      audience: {
        ...targeting.audience,
        lists: audienceGroups
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setRetargetingSegment = (segment: RetargetingSegmentType | null, audienceGroups?: AudienceGroup[]) => {
    const newTargeting = {
      ...targeting,
      audience: {
        ...targeting.audience,
        ...(audienceGroups ? { lists: audienceGroups } : {}),
        retargetingSegment: segment
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setAppAndSiteChannelProps = (values: string[]) => {
    const newTargeting = {
      ...targeting,
      channels: values
    };
    emitTargetingIfApply(newTargeting);
  };

  const setMediaQualityProps = (prop: any, value: any, enabled?: boolean) => {
    const newTargeting = {
      ...targeting,
      mediaQuality: {
        ...targeting.mediaQuality,
        ...(enabled !== undefined ? { enabled } : {}),
        [prop]: value
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setIabCatVersion = (prop: string, value: number) => {
    const newTargeting = {
      ...targeting,
      categories: DEFAULT_APP_AND_SITES_CATEGORIES.categories,
      iabCatVersion: {
        ...targeting.iabCatVersion,
        [prop]: value
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setTemperatureAndWeatherProps = (prop: any, value: any) => {
    const newTargeting = {
      ...targeting,
      temperatureAndWeather: {
        ...targeting.temperatureAndWeather,
        [prop]: value
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setDeviceTargetingProps = (prop: string, value: any) => {
    const newTargeting = {
      ...targeting,
      targetUsers: {
        ...targeting.targetUsers,
        [prop]: value
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const setUnknownIncluded = (prop: string, value: boolean) => {
    const newTargeting = {
      ...targeting,
      unknownIncluded: {
        ...targeting.unknownIncluded,
        [prop]: value
      }
    };
    emitTargetingIfApply(newTargeting);
  };

  const resetSection = (id: string) => {
    let resetValues: Partial<TargetingWithRetargetingType> = {};
    switch (id) {
      case 'demography':
        resetValues = {
          ...DEFAULT_DEMOGRAPHY,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            ...DEFAULT_DEMOGRAPHY.unknownIncluded
          }
        };
        break;
      case 'demographyAgeGender':
        resetValues = {
          ages: DEFAULT_DEMOGRAPHY.ages,
          gender: DEFAULT_DEMOGRAPHY.gender,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            userAge: DEFAULT_DEMOGRAPHY.unknownIncluded.userAge,
            userGender: DEFAULT_DEMOGRAPHY.unknownIncluded.userGender
          }
        };
        break;
      case 'appAndSitesCategories':
        resetValues = {
          ...DEFAULT_APP_AND_SITES_CATEGORIES,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            ...DEFAULT_APP_AND_SITES_CATEGORIES.unknownIncluded
          }
        };
        break;
      case 'contentCategories':
        resetValues = {
          contextualTargeting: {
            ...targeting.contextualTargeting,
            iabCategories: DEFAULT_CONTEXTUAL_TARGETING.contextualTargeting.iabCategories,
            iasCategories: DEFAULT_CONTEXTUAL_TARGETING.contextualTargeting.iasCategories
          },
          unknownIncluded: {
            ...targeting.unknownIncluded,
            contentCategories: DEFAULT_CONTEXTUAL_TARGETING.unknownIncluded.contentCategories,
            thirdPartyContentCategories: DEFAULT_CONTEXTUAL_TARGETING.unknownIncluded.thirdPartyContentCategories
          }
        };
        break;
      case 'contextualTargeting':
        resetValues = {
          contextualTargeting: DEFAULT_CONTEXTUAL_TARGETING.contextualTargeting,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            ...DEFAULT_CONTEXTUAL_TARGETING.unknownIncluded
          }
        };
        break;
      case 'lqiQualityAssurance':
        resetValues = {
          ...DEFAULT_LQI
        };
        break;
      case 'mobileDeviceAndConnectivity':
        resetValues = {
          ...DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            ...DEFAULT_MOBILE_DEVICE_AND_CONNECTIVITY.unknownIncluded
          }
        };
        break;
      case 'supplySource':
        resetValues = {
          ...(familyType === 'dooh' ? DEFAULT_DOOH_AD_EXCHANGES : DEFAULT_AD_EXCHANGES)
        };
        break;
      case 'mediaQualityAssuranceSystem':
        resetValues = getMediaQualityResetValues(targeting, user, userRoles, DEFAULT_MEDIA_QUALITY);
        break;
      case 'appAndSiteChannel':
        resetValues = {
          channels: DEFAULT_APP_AND_SITE_CHANNEL.channels
        };
        break;
      case 'appAndSiteMarketplace':
        resetValues = {
          appSiteConfiguration: { ...DEFAULT_APP_AND_SITE_CONFIGURATION, ...EMPTY_STRICT_SEARCHES },
          deals: DEFAULT_DEALS_CONFIGURATION.deals,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            domain: true
          }
        };
        break;
      case 'temperatureAndWeather':
        resetValues = {
          temperatureAndWeather: DEFAULT_TEMPERATURE_AND_WEATHER.temperatureAndWeather,
          unknownIncluded: {
            ...targeting.unknownIncluded,
            ...DEFAULT_TEMPERATURE_AND_WEATHER.unknownIncluded
          }
        };
        break;
      case 'deviceTargeting':
        resetValues = {
          targetUsers: DEFAULT_DEVICE_TARGETING.targetUsers
        };
        break;
      case 'audienceTargeting':
        resetValues = {
          audience: {
            ...targeting.audience,
            lists: DEFAULT_AUDIENCE.lists,
            retargetingSegment: null
          }
        };
        break;
      case 'retargeting':
        resetValues = {
          retargeting: DEFAULT_RETARGETING
        };
        break;
      default:
        return;
    }
    const newTargeting = {
      ...targeting,
      ...resetValues
    };
    emitTargetingIfApply(newTargeting);
  };

  const normalizedUser = {
    ...user,
    _id: user?._id || user?.id
  };

  return (
    <TargetingContext.Provider
      value={{
        budget,
        countries,
        currency,
        familyType,
        resetSection,
        setAppAndSiteChannelProps,
        setAppSiteConfigurationProps,
        setAudience,
        setDeals,
        setDeviceTargetingProps,
        setIabCatVersion,
        setMediaQualityProps,
        setProp,
        setRetargetingSegment,
        setTemperatureAndWeatherProps,
        setUnknownIncluded,
        showDeals,
        targeting,
        user: normalizedUser,
        userRoles
      }}
    >
      {children}
    </TargetingContext.Provider>
  );
}

export default TargetingProvider;
